// MARK:- OS 2.0 cart drawer controller. Document-level delegation (no wrapping
// custom element — wrapping legacy markup breaks the existing layout). Listens
// for cart:added from <as-product-form>, mutates the cart via /cart/*.js, and
// re-renders the cart drawer in place using Shopify's Section Rendering API.
//
// File naming: hand-edited asset, NOT a webpack/gulp build output. Loaded once
// per page from sections/cart-drawer.liquid via:
//
//   <script src="{{ 'component-cart.js' | asset_url }}" type="module"></script>
//
// MARK:- Note hooks: root data-as-cart-note, parent data-as-cart-sku, child data-as-cart-bow-child. Bow hooks: [data-as-cart-children] wraps .cart__item--child[data-as-cart-child-of] or [data-as-cart-quick-add][data-as-cart-parent-line-key][data-as-cart-part-of].
// Markup contract:
//
//   <div id="cart" class="drawer cart" data-as-cart data-as-cart-section-id="…">
//     <ul>
//       <li>
//         <div class="cart__item" data-as-cart-line="1" data-as-cart-line-key="…">
//           <div class="quantity-picker">
//             <button data-as-cart-qty-decrement></button>
//             <input data-as-cart-qty-input>
//             <button data-as-cart-qty-increment></button>
//           </div>
//           <button data-as-cart-remove>Remove</button>
//         </div>
//       </li>
//     </ul>
//     <div class="cart__recommended"
//       data-as-cart-recommendations
//       data-as-cart-recommendations-anchor="…"
//       data-as-cart-recommendations-count="…">
//     </div>
//     <button data-as-cart-quick-add data-as-cart-variant-id="…">Add</button>
//     <textarea data-as-cart-extra-textarea data-as-cart-extra-key="gift-note"></textarea>
// MARK:- Cart extras strip emoji live; merchant copy sits above Confirm and is shown after a removal.
//     <span data-as-cart-extra-emoji-note hidden></span>
//     <button data-as-cart-extra-confirm data-as-cart-extra-key="gift-note">Confirm</button>
//     <button data-as-cart-extra-remove="gift-note">Remove</button>
//   </div>
//
// Behavior:
// - Cart mutations (qty +/-, remove, quick-add, gift-note save/remove) POST to
//   /cart/{change|add|update}.js with `sections: 'cart-drawer'`. Response carries
//   the cart JSON (item_count → header swap) and the rendered section HTML
//   (drawer body swap).
// - cart:added event from PDP <as-product-form> triggers a /cart.js?sections=…
//   refresh through the same applyResponse path.
// - Mutations are serialized via a promise chain so rapid clicks don't interleave.
// - Recommendations slot is hydrated client-side from /recommendations/products
//   using the section_id endpoint with Shopify's `recommendations` Liquid object.
// - Storefront Events & Actions: performCartUpdate is exposed as
//   window.__asCartActionHandler and registered as the Shopify.actions.updateCart
//   handler by snippets/storefront-actions.liquid. Theme-initiated mutations
//   route through Shopify.actions.updateCart so the standard shopify:cart:*
//   events auto-emit; app-initiated updateCart/openCart calls run our pipeline.
// - Idempotent: window.__asCartInit guard prevents double-binding when the
//   section reloads in the customizer.

(() => {
  if (window.__asCartInit) return;
  window.__asCartInit = true;

  const SECTION_CART = 'cart-drawer';
  const SECTION_RECS = 'cart-recommendations';

  // MARK:- Shopify's hard-coded cart-cap strings. Both fire when a customer hits the per-line limit (one for "added < requested", one for "already at max"). Substituted with settings.cart_quantity_max_message via the cart-drawer error surface when either matches.
  const SHOPIFY_LIMIT_RE = /(?:Only (\d+) items? were added to your cart due to availability|The maximum quantity of this item is already in your cart)\./i;

  // MARK:- Theme-level quantity limits read off <body data-as-qty-*>. Cart line min stays 0 (not settings.cart_quantity_min) so the - button at qty 1 still removes the line — that's how Shopify's cart AJAX has always worked here.
  function readQtyLimits() {
    const min = 0;
    const rawMax = parseInt(document.body.dataset.asQtyMax, 10);
    const max = Number.isFinite(rawMax) && rawMax > 0 ? rawMax : Infinity;
    const message = document.body.dataset.asQtyMessage || '';
    return { min, max, message };
  }

  function showCartError(raw) {
    const target = document.querySelector('[data-as-cart-error]');
    if (!target) return;
    const { message } = readQtyLimits();
    const display = (message && SHOPIFY_LIMIT_RE.test(raw)) ? message : raw;
    target.textContent = display || '';
    target.hidden = !display;
  }

  function clearCartError() {
    const target = document.querySelector('[data-as-cart-error]');
    if (!target) return;
    target.textContent = '';
    target.hidden = true;
  }

  // MARK:- Per-line error surface. Targets [data-as-cart-limit-text] on the matching line by variant id and shows the resolved "Limit X per order" copy (data-as-cart-limit-message). Only used for SHOPIFY_LIMIT_RE-matching errors AND when the slot exists for that line; otherwise falls through to the drawer-level error slot.
  function showLineError(variantId, raw) {
    const isLimitError = SHOPIFY_LIMIT_RE.test(raw);
    const line = variantId
      ? document.querySelector(`[data-as-cart-line][data-as-cart-variant-id="${CSS.escape(String(variantId))}"]`)
      : null;
    const target = line && line.querySelector('[data-as-cart-limit-text]');
    const msg = target ? (target.getAttribute('data-as-cart-limit-message') || '') : '';
    if (!isLimitError || !target || !msg) {
      showCartError(raw);
      return;
    }
    target.textContent = msg;
    target.removeAttribute('hidden');
  }

  function clearLineError(variantId) {
    const line = variantId
      ? document.querySelector(`[data-as-cart-line][data-as-cart-variant-id="${CSS.escape(String(variantId))}"]`)
      : null;
    if (!line) return;
    const target = line.querySelector('[data-as-cart-limit-text]');
    if (!target) return;
    target.textContent = '';
    target.setAttribute('hidden', '');
  }

  // MARK:- Per-line +/- button toggler. Used at click-time (instant feedback) and after smart-merge (server-truth). Buttons aren't focused even if the input is, so this runs unconditionally. - is visually disabled at qty 1 (Remove button is the delete affordance); + is disabled at the per-line max (input's max attr — set from the per-product `quantity_limit` metafield or theme cap), with a fallback to the body-level cap. Also toggles the per-line "Limit X per order" indicator: shown at atMax, hidden otherwise.
  function refreshLineQtyButtonState(lineEl, value, limits) {
    const lim = limits || readQtyLimits();
    const dec = lineEl.querySelector('[data-as-cart-qty-decrement]');
    const inc = lineEl.querySelector('[data-as-cart-qty-increment]');
    const input = lineEl.querySelector('[data-as-cart-qty-input]');
    const inputMax = input ? parseInt(input.getAttribute('max'), 10) : NaN;
    const lineMax = Number.isFinite(inputMax) && inputMax > 0 ? inputMax : lim.max;
    const atMin = value <= 1;
    const atMax = value >= lineMax;
    if (dec) {
      dec.disabled = atMin;
      if (atMin) dec.setAttribute('aria-disabled', 'true');
      else dec.removeAttribute('aria-disabled');
    }
    if (inc) {
      inc.disabled = atMax;
      if (atMax) inc.setAttribute('aria-disabled', 'true');
      else inc.removeAttribute('aria-disabled');
    }
    const limit = lineEl.querySelector('[data-as-cart-limit-text]');
    if (limit) {
      const msg = limit.getAttribute('data-as-cart-limit-message') || '';
      if (atMax && msg) {
        limit.textContent = msg;
        limit.removeAttribute('hidden');
      } else {
        limit.textContent = '';
        limit.setAttribute('hidden', '');
      }
    }
  }

  let queue = Promise.resolve();
  // MARK:- Optional ctx.variantId routes the rejection to the matching line's [data-as-cart-limit-text] slot rather than the drawer-level error. Cart-line +/- and quantity input changes pass it; non-line mutations (cart-extras, attributes) leave ctx undefined and fall through to showCartError.
  const enqueue = (fn, ctx) => {
    queue = queue.then(fn).catch((err) => {
      console.error('[as-cart]', err);
      const message = (err && err.message) || '';
      if (ctx && ctx.variantId) {
        showLineError(ctx.variantId, message);
      } else {
        showCartError(message);
      }
    });
    return queue;
  };

  function getCartSectionInstanceId() {
    const el = document.querySelector('[data-as-cart]');
    return el ? el.dataset.asCartSectionId : null;
  }

  async function postCart(endpoint, body) {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTION_CART }),
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      throw new Error(payload.description || payload.message || `Cart error ${res.status}`);
    }
    // MARK:- Successful response clears any stale error from the drawer.
    clearCartError();
    return res.json();
  }

  function setCountBadges(count) {
    const text = `(${count})`;
    document.querySelectorAll('[data-as-cart-count]').forEach((el) => { el.textContent = text; });
  }

  // MARK:- Gift-note-only responses preserve line grouping; they do not mutate cart lines.
  function applyResponse(payload, preserveLines = false) {
    if (!payload) return;
    // /cart/update.js + /cart/change.js carry item_count directly.
    if (typeof payload.item_count === 'number') {
      setCountBadges(payload.item_count);
    }
    const drawerHtml = payload.sections && payload.sections[SECTION_CART];
    if (!drawerHtml) return;

    const tmp = document.createElement('div');
    tmp.innerHTML = drawerHtml.trim();
    const newSection = tmp.firstElementChild;
    if (!newSection) return;

    const oldCart = document.querySelector('#cart');
    const newCart = newSection.querySelector('#cart');

    // MARK:- Detect structural transitions (empty ↔ populated) — those need a
    // full innerHTML swap because the children differ entirely. Everything
    // else (qty +/-, line removed but cart non-empty, threshold/total updates)
    // gets a targeted smart merge so DOM nodes, focus, and animations survive.
    const oldHasItems = !!oldCart && !!oldCart.querySelector('.cart__items');
    const newHasItems = !!newCart && !!newCart.querySelector('.cart__items');
    const structuralChange = oldHasItems !== newHasItems;

    if (oldCart && newCart && !structuralChange) {
      smartMerge(oldCart, newCart, preserveLines);
      // MARK:- Keep the stored note current after drawer renders.
      if (newCart.dataset.asCartNote != null) oldCart.dataset.asCartNote = newCart.dataset.asCartNote;
      if (newCart.dataset.asCartItemCount != null) {
        oldCart.dataset.asCartItemCount = newCart.dataset.asCartItemCount;
        if (typeof payload.item_count !== 'number') {
          setCountBadges(newCart.dataset.asCartItemCount);
        }
      }
    } else if (oldCart && newCart) {
      // Empty ↔ populated: full inner swap is fine — there's no live qty
      // interaction to preserve and the children differ entirely.
      oldCart.innerHTML = newCart.innerHTML;
      // MARK:- Keep the stored note current after drawer renders.
      if (newCart.dataset.asCartNote != null) oldCart.dataset.asCartNote = newCart.dataset.asCartNote;
      if (newCart.dataset.asCartItemCount != null) {
        oldCart.dataset.asCartItemCount = newCart.dataset.asCartItemCount;
        if (typeof payload.item_count !== 'number') {
          setCountBadges(newCart.dataset.asCartItemCount);
        }
      }
    } else {
      // Fallback: section wrapper swap (initial render only).
      const instanceId = getCartSectionInstanceId();
      if (instanceId) {
        const wrapper = document.getElementById(`shopify-section-${instanceId}`);
        if (wrapper) wrapper.outerHTML = newSection.outerHTML;
      }
    }

    refreshRecommendations();
    // MARK:- Reconcile bow fulfilment after every drawer render.
    reconcileBowNote();
  }

  // MARK:- Targeted DOM merge for the common case (qty +/-, line remove that
  // doesn't empty the cart). Walks specific elements instead of replacing
  // innerHTML so that unaffected lines, the recommendations splide, and any
  // in-flight focus/animation state are preserved.
  function smartMerge(oldCart, newCart, preserveLines = false) {
    // Header "Basket (N)"
    const oldHeader = oldCart.querySelector('.drawer__top strong.cta');
    const newHeader = newCart.querySelector('.drawer__top strong.cta');
    if (oldHeader && newHeader && oldHeader.textContent !== newHeader.textContent) {
      oldHeader.textContent = newHeader.textContent;
    }

    // Threshold bar (rewards progress + tier labels)
    const oldThreshold = oldCart.querySelector('.cart__discount-threshold');
    const newThreshold = newCart.querySelector('.cart__discount-threshold');
    if (oldThreshold && newThreshold) {
      if (oldThreshold.innerHTML !== newThreshold.innerHTML) {
        oldThreshold.innerHTML = newThreshold.innerHTML;
      }
    } else if (newThreshold && !oldThreshold) {
      // Bar appeared (e.g., setting toggled). Insert ahead of the line list.
      const items = oldCart.querySelector('.cart__items');
      if (items) items.insertBefore(newThreshold.cloneNode(true), items.firstChild);
    } else if (oldThreshold && !newThreshold) {
      oldThreshold.remove();
    }

    // Cart lines — keyed by data-as-cart-line-key (Shopify cart line key).
    const oldList = oldCart.querySelector('.cart__items ul');
    const newList = newCart.querySelector('.cart__items ul');
    // MARK:- A gift-note save must not replace bow markers with note-response line markup.
    if (oldList && newList && !preserveLines) {
      const oldByKey = new Map();
      // MARK:- Reconcile top-level lines only; mergeLine swaps bow children wholesale.
      oldList.querySelectorAll(':scope > li > [data-as-cart-line-key]').forEach((el) => {
        oldByKey.set(el.dataset.asCartLineKey, el);
      });

      const newKeys = new Set();
      const desiredOrder = [];
      newList.querySelectorAll(':scope > li').forEach((newLi) => {
        const newLineEl = newLi.querySelector(':scope > [data-as-cart-line-key]');
        if (!newLineEl) return;
        const key = newLineEl.dataset.asCartLineKey;
        newKeys.add(key);
        const oldLineEl = oldByKey.get(key);
        if (oldLineEl) {
          mergeLine(oldLineEl, newLineEl);
          // Also sync data-as-cart-line index (Shopify's 1-based line number
          // can shift when lines above are removed).
          if (newLineEl.dataset.asCartLine && oldLineEl.dataset.asCartLine !== newLineEl.dataset.asCartLine) {
            oldLineEl.dataset.asCartLine = newLineEl.dataset.asCartLine;
          }
          desiredOrder.push(oldLineEl.closest('li'));
        } else {
          // New line — clone in.
          const cloned = newLi.cloneNode(true);
          oldList.appendChild(cloned);
          desiredOrder.push(cloned);
        }
      });

      // Remove lines no longer in the response.
      oldByKey.forEach((oldLineEl, key) => {
        if (!newKeys.has(key)) {
          const li = oldLineEl.closest('li');
          if (li) li.remove();
        }
      });

      // Re-order if needed (cheap when already correct — appendChild on a node
      // already in the right place is a no-op).
      desiredOrder.forEach((li) => {
        if (li && li.parentNode === oldList) oldList.appendChild(li);
      });
    }

    // Cart footer total
    const oldTotal = oldCart.querySelector('[data-as-cart-total]');
    const newTotal = newCart.querySelector('[data-as-cart-total]');
    if (oldTotal && newTotal && oldTotal.textContent !== newTotal.textContent) {
      oldTotal.textContent = newTotal.textContent;
    }

    // Recommendations slot — only touch attributes if the anchor product
    // changed; the contents are populated/refreshed by refreshRecommendations.
    const oldRecs = oldCart.querySelector('[data-as-cart-recommendations]');
    const newRecs = newCart.querySelector('[data-as-cart-recommendations]');
    if (oldRecs && newRecs) {
      const oldAnchor = oldRecs.dataset.asCartRecommendationsAnchor;
      const newAnchor = newRecs.dataset.asCartRecommendationsAnchor;
      if (oldAnchor !== newAnchor) {
        oldRecs.dataset.asCartRecommendationsAnchor = newAnchor || '';
        oldRecs.innerHTML = ''; // forces refreshRecommendations to repopulate
      }
    }

    // MARK:- Cart-extras (gift note / gift wrapping) — sync the row's filled
    // state and the displayed value, plus the corresponding sub-drawer's
    // textarea so a re-open shows the latest saved value.
    oldCart.querySelectorAll('[data-as-cart-extra]').forEach((oldRow) => {
      const key = oldRow.dataset.asCartExtra;
      const newRow = newCart.querySelector(`[data-as-cart-extra="${CSS.escape(key)}"]`);
      if (!newRow) return;
      if (oldRow.className !== newRow.className) {
        oldRow.className = newRow.className;
      }
      const oldVal = oldRow.querySelector('[data-as-cart-extra-value]');
      const newVal = newRow.querySelector('[data-as-cart-extra-value]');
      if (oldVal && newVal && oldVal.textContent !== newVal.textContent) {
        oldVal.textContent = newVal.textContent;
      }
    });

    // Sub-drawer textareas — sync server-stored value and reset the Confirm
    // button's disabled state to match (don't stomp the textarea while focused).
    oldCart.querySelectorAll('[data-as-cart-extra-sub-drawer]').forEach((oldSub) => {
      const key = oldSub.dataset.asCartExtraSubDrawer;
      const newSub = newCart.querySelector(`[data-as-cart-extra-sub-drawer="${CSS.escape(key)}"]`);
      if (!newSub) return;
      const oldTa = oldSub.querySelector('[data-as-cart-extra-textarea]');
      const newTa = newSub.querySelector('[data-as-cart-extra-textarea]');
      if (oldTa && newTa && document.activeElement !== oldTa && oldTa.value !== newTa.value) {
        oldTa.value = newTa.value;
      }
      const oldConfirm = oldSub.querySelector('[data-as-cart-extra-confirm]');
      const newConfirm = newSub.querySelector('[data-as-cart-extra-confirm]');
      if (oldConfirm && newConfirm) {
        oldConfirm.disabled = newConfirm.disabled;
      }
    });
  }

  function mergeLine(oldLine, newLine) {
    // Class list (gift / inactive states change with thresholds).
    if (oldLine.className !== newLine.className) {
      oldLine.className = newLine.className;
    }
    // Line price (shown on the right).
    const oldPrice = oldLine.querySelector('.cart__item__content__right .price');
    const newPrice = newLine.querySelector('.cart__item__content__right .price');
    if (oldPrice && newPrice && oldPrice.textContent !== newPrice.textContent) {
      oldPrice.textContent = newPrice.textContent;
    }
    // MARK:- Defensive: sync the purchase-type select's value in case an in-place merge keeps the old node (changing a plan usually changes the line key → clone path, but a same-key re-render shouldn't show a stale selection).
    const oldPlan = oldLine.querySelector('[data-as-cart-selling-plan-input]');
    const newPlan = newLine.querySelector('[data-as-cart-selling-plan-input]');
    if (oldPlan && newPlan && document.activeElement !== oldPlan && oldPlan.value !== newPlan.value) {
      oldPlan.value = newPlan.value;
    }
    // Qty input — only sync if the user isn't focused there (we don't want to
    // stomp on a number they're typing).
    const oldQty = oldLine.querySelector('[data-as-cart-qty-input]');
    const newQty = newLine.querySelector('[data-as-cart-qty-input]');
    if (oldQty && newQty && document.activeElement !== oldQty && oldQty.value !== newQty.value) {
      oldQty.value = newQty.value;
    }
    // MARK:- Defensive: re-apply readonly + aria-readonly in case a third-party SRA response stripped them. The snippet always emits these.
    if (oldQty) {
      if (!oldQty.hasAttribute('readonly')) oldQty.setAttribute('readonly', '');
      if (!oldQty.hasAttribute('aria-readonly')) oldQty.setAttribute('aria-readonly', 'true');
    }
    // MARK:- Sync the resolved limit-message text from the new render so theme-setting / metafield changes propagate without a full innerHTML swap. Done BEFORE refreshLineQtyButtonState so the indicator picks up the new message immediately. Visibility is owned by refreshLineQtyButtonState (atMax) + showLineError (422).
    const oldLimit = oldLine.querySelector('[data-as-cart-limit-text]');
    const newLimit = newLine.querySelector('[data-as-cart-limit-text]');
    if (oldLimit && newLimit) {
      const newMessage = newLimit.getAttribute('data-as-cart-limit-message') || '';
      if (oldLimit.getAttribute('data-as-cart-limit-message') !== newMessage) {
        oldLimit.setAttribute('data-as-cart-limit-message', newMessage);
      }
    }

    // MARK:- Re-evaluate +/- disabled state against server-truth qty. Runs unconditionally — buttons aren't focused even if the input is.
    if (oldQty || newQty) {
      const truthQty = parseInt((oldQty || newQty).value, 10) || 0;
      refreshLineQtyButtonState(oldLine, truthQty);
    }

    // MARK:- Sub-element add-ons (Pairs Well With, V2 set/bundle) live as
    // siblings to .cart__item inside the parent <li>. Targeting (e.g.
    // pairs_target_key) is computed server-side, so swap the add-ons wholesale
    // from the new render rather than trying to reconcile in place.
    const oldLi = oldLine.closest('li');
    const newLi = newLine.closest('li');
    if (oldLi && newLi) {
      // MARK:- Children wrapper (bow line rows OR the Add-a-bow row) is server-decided; swap it wholesale like the add-ons. Cases: replace, appear, disappear.
      const oldChildren = oldLi.querySelector(':scope > [data-as-cart-children]');
      const newChildren = newLi.querySelector(':scope > [data-as-cart-children]');
      if (oldChildren && newChildren) {
        if (oldChildren.innerHTML !== newChildren.innerHTML) oldChildren.innerHTML = newChildren.innerHTML;
      } else if (newChildren) {
        oldLine.insertAdjacentElement('afterend', newChildren.cloneNode(true));
      } else if (oldChildren) {
        oldChildren.remove();
      }
      oldLi.querySelectorAll(':scope > .cart__add-on-item').forEach((el) => el.remove());
      newLi.querySelectorAll(':scope > .cart__add-on-item').forEach((newEl) => {
        oldLi.appendChild(newEl.cloneNode(true));
      });
    }
  }

  // GET /cart.js does NOT honor ?sections=. Use POST /cart/update.js with no
  // mutating fields — Shopify returns the full cart plus the requested
  // section HTML in one response.
  async function fetchCartSnapshot() {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ sections: SECTION_CART }),
    });
    if (!res.ok) throw new Error(`Cart refresh ${res.status}`);
    return res.json();
  }

  async function refreshFromCart() {
    return enqueue(async () => {
      applyResponse(await fetchCartSnapshot());
    });
  }

  // MARK:- Build the actions Result's cart with the runtime's own (non-configurable)
  // Shopify.actions.getCart() — it returns the exact CartSummary shape the
  // standard-actions/standard-events runtime dereferences (hand-mapped Cart AJAX
  // JSON does not: the event resolver maps over line fields we can't reliably
  // guess). Returns null when the actions runtime is absent (direct-fallback
  // path, where nothing consumes Result.cart).
  async function readActionsCart() {
    if (window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.getCart === 'function') {
      const res = await window.Shopify.actions.getCart();
      if (res && res.cart) return res.cart;
    }
    return null;
  }

  async function refreshRecommendations(force) {
    const slot = document.querySelector('[data-as-cart-recommendations]');
    if (!slot) return;
    const anchor = slot.dataset.asCartRecommendationsAnchor;
    if (!anchor) return;
    // MARK:- Skip refetch if the slot already has content. smartMerge clears
    // the slot when the anchor product changes; otherwise we leave the live
    // splide alone so the user's scroll position and Splide's mount survive.
    // force=true (successful add) bypasses the skip: cart-recommendations.liquid
    // excludes in-cart products server-side, so a refetch drops the just-added
    // product out of the carousel.
    if (!force && slot.firstElementChild) return;
    const count = slot.dataset.asCartRecommendationsCount || 6;
    const url = `/recommendations/products?section_id=${SECTION_RECS}&product_id=${encodeURIComponent(anchor)}&limit=${encodeURIComponent(count)}&intent=related`;
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      slot.innerHTML = await res.text();
      mountRecommendationsSplide(slot);
    } catch (err) {
      console.error('[as-cart] recommendations', err);
    }
  }

  // Splide is loaded globally via theme.liquid; the legacy theme.js inits it
  // for static `.product-card-slider.splide` markup at DOMContentLoaded. Our
  // recs slot is filled after that, so we mount a fresh Splide instance here
  // using the same options as productCardSlider() in src/js/theme.js.
  function mountRecommendationsSplide(slot) {
    if (typeof window.Splide !== 'function') return;
    const splideEl = slot.querySelector('.product-card-slider.splide');
    if (!splideEl) return;
    if (splideEl.classList.contains('is-initialized')) return;
    try {
      // MARK:- perPage:1 (not 2) — autoWidth:true + perPage:2 caps the last reachable index at `length - 2`, leaving the last slide partly cut off. See productCardSlider() in src/js/theme.js for the same fix.
      new window.Splide(splideEl, {
        type: 'slide',
        speed: 300,
        perPage: 1,
        perMove: 1,
        autoWidth: true,
        flickPower: 300,
        arrows: splideEl.classList.contains('has-arrows'),
        pagination: false,
      }).mount();
    } catch (err) {
      console.error('[as-cart] splide mount', err);
    }
  }

  // MARK:- Storefront Actions integration. performCartUpdate is the single
  // mutation primitive: it translates a Shopify.actions.updateCart payload
  // ({ lines: [{ merchandiseId | id, quantity }] } / note / attributes) onto the
  // existing Cart AJAX endpoints, keeping the one-round-trip sections render,
  // and resolves with an actions-style Result ({ cart, userErrors, warnings }).
  // It never rejects — failures surface through the existing drawer error UX
  // and come back as userErrors. snippets/storefront-actions.liquid registers
  // it as the configured updateCart handler via window.__asCartActionHandler,
  // so app-initiated Shopify.actions.updateCart calls render our drawer too.
  function variantIdFrom(merchandiseId) {
    const match = String(merchandiseId).match(/(\d+)$/);
    return match ? match[1] : String(merchandiseId);
  }

  // MARK:- Payload objects our pipeline has already mutated the cart for.
  // routeCartUpdate only falls back to the direct pipeline on a rejection when
  // the payload is NOT in this set (i.e. Shopify's runtime rejected it before
  // our handler ran) — prevents double mutations.
  const handledPayloads = new WeakSet();

  // MARK:- Second "did our handler run?" signal alongside the WeakSet — the
  // actions runtime may clone payloads before invoking the handler, which would
  // defeat the object-identity check. Callers snapshot the counter before the
  // actions call; a bumped counter after a rejection means a mutation ran, so
  // never retry (errs toward no-duplicate over retry).
  let handlerRuns = 0;
  window.__asCartHandlerRuns = () => handlerRuns;

  function performCartUpdate(payload, options, defaultHandler) {
    handlerRuns += 1;
    const detail = (options && options.event && options.event.detail) || {};
    const result = { cart: null, userErrors: [], warnings: [], detail: {} };
    if (payload && typeof payload === 'object') handledPayloads.add(payload);
    return enqueue(async () => {
      try {
        // MARK:- Payload fields we can't express on the AJAX API (e.g.
        // discountCodes) delegate wholesale to Shopify's default handler, then
        // re-render the drawer from a fresh snapshot. Inline (not
        // refreshFromCart) — nested enqueue would deadlock the promise chain.
        const knownKeys = ['lines', 'note', 'attributes'];
        const unknownKeys = Object.keys(payload || {}).filter((k) => !knownKeys.includes(k));
        if (unknownKeys.length && typeof defaultHandler === 'function') {
          const delegated = await defaultHandler();
          applyResponse(await fetchCartSnapshot());
          if (delegated && typeof delegated === 'object') Object.assign(result, delegated);
          return;
        }

        let data = null;
        let didAdd = false;
        const lines = Array.isArray(payload && payload.lines) ? payload.lines : [];
        const adds = lines.filter((l) => l && l.merchandiseId != null);
        const changes = lines.filter((l) => l && l.merchandiseId == null && l.id != null);
        if (adds.length) {
          data = await postCart('/cart/add.js', {
            items: adds.map((l) => ({ id: variantIdFrom(l.merchandiseId), quantity: l.quantity == null ? 1 : l.quantity })),
          });
          didAdd = true;
        }
        for (const line of changes) {
          data = await postCart('/cart/change.js', { id: line.id, quantity: line.quantity });
        }
        if (payload && (payload.note !== undefined || payload.attributes !== undefined)) {
          const body = {};
          if (payload.note !== undefined) body.note = payload.note;
          if (payload.attributes !== undefined) body.attributes = payload.attributes;
          data = await postCart('/cart/update.js', body);
        }
        if (data) {
          // MARK:- Preserve cart lines when only the gift note and its legacy attribute change.
          const preserveLines = detail.source === 'cart-extras' && payload.note !== undefined && !lines.length;
          applyResponse(data, preserveLines);
          if (detail.variantId) clearLineError(detail.variantId);
          if (didAdd) refreshRecommendations(true);
        }
        // MARK:- A failed getCart read must NOT turn a successful mutation into
        // a userError — log and move on.
        try {
          result.cart = await readActionsCart();
        } catch (readErr) {
          console.error('[as-cart] cart read', readErr);
        }
      } catch (err) {
        console.error('[as-cart]', err);
        const message = (err && err.message) || '';
        if (detail.variantId) showLineError(detail.variantId, message);
        else showCartError(message);
        result.userErrors = [{ message }];
        // MARK:- Error Results still carry the current cart (docs shape: cart
        // PLUS userErrors) — and the runtime dereferences it either way.
        try {
          result.cart = await readActionsCart();
        } catch (readErr) {
          console.error('[as-cart] cart read', readErr);
        }
      }
    }).then(() => result);
  }
  window.__asCartActionHandler = performCartUpdate;
  // MARK:- Lets routed callers (form components) distinguish "our pipeline ran, the actions runtime failed afterwards" (mutation applied — sync UI, no retry) from a pre-handler rejection (safe to fall back to the direct path).
  window.__asCartPayloadHandled = (payload) => handledPayloads.has(payload);

  // MARK:- Shared drawer opener — also serves the configured openCart action.
  window.__asOpenCartDrawer = () => {
    const drawer = document.querySelector('#cart.drawer');
    if (!drawer || drawer.classList.contains('is-open')) return;
    const trigger = document.querySelector('[data-as-drawer="cart"]:not([data-as-drawer-hover])');
    if (trigger) trigger.click();
  };

  // MARK:- Theme-initiated mutations route through Shopify.actions.updateCart
  // when our configure() won (window.__asActionsConfigured, set by
  // snippets/storefront-actions.liquid) so the standard shopify:cart:* events
  // auto-emit for app listeners. Otherwise (API absent, configure lost, or a
  // pre-handler validation rejection) they fall back to the direct pipeline.
  function routeCartUpdate(payload, context, detail) {
    const options = { event: { context, detail } };
    const configured = window.__asActionsConfigured && window.__asActionsConfigured.updateCart;
    if (configured && window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.updateCart === 'function') {
      const runsBefore = handlerRuns;
      return window.Shopify.actions.updateCart(payload, options).catch((err) => {
        console.error('[as-cart] updateCart action', err);
        if (!handledPayloads.has(payload) && handlerRuns === runsBefore) return performCartUpdate(payload, options);
        return { cart: null, userErrors: [{ message: (err && err.message) || '' }], warnings: [], detail: {} };
      });
    }
    return performCartUpdate(payload, options);
  }

  // MARK:- variantId is optional but lets the error path target this line's per-line error slot. Successful change clears any prior per-line error for that variant. postCart's clearCartError() already handles drawer-level.
  function changeLine(lineKey, quantity, variantId) {
    return routeCartUpdate({ lines: [{ id: lineKey, quantity }] }, 'cart', { source: 'as-cart', variantId });
  }
  // MARK:- Bow follows parent quantity changes and removal; include child keys at qty 0 through updateCart.
  function childLineKeys(lineEl) {
    if (lineEl.hasAttribute('data-as-cart-child-of')) return [];
    const wrap = lineEl.closest('li')?.querySelector(':scope > [data-as-cart-children]');
    if (!wrap) return [];
    return Array.from(wrap.querySelectorAll('[data-as-cart-child-of]')).map((el) => el.dataset.asCartLineKey);
  }
  function changeLineWithChildren(lineEl, quantity, variantId) {
    const lines = [{ id: lineEl.dataset.asCartLineKey, quantity }];
    childLineKeys(lineEl).forEach((key) => lines.push({ id: key, quantity }));
    return routeCartUpdate({ lines }, 'cart', { source: 'as-cart', variantId });
  }

  function addVariant(id, quantity) {
    return routeCartUpdate({ lines: [{ merchandiseId: id, quantity }] }, 'cart', { source: 'quick-add' });
  }
  // MARK:- Change a line's purchase type. selling_plan isn't expressible in the Shopify.actions.updateCart payload, so this stays on the direct /cart/change.js pipeline (same as forms carrying selling plans). Empty/falsy sellingPlanId reverts the line to one-time (selling_plan: null). quantity MUST be passed — change.js assumes quantity:1 when omitted. Changing the plan changes the line key, so applyResponse → smartMerge clones in the freshly rendered line.
  function changeLineSellingPlan(lineKey, quantity, sellingPlanId, variantId) {
    return enqueue(async () => {
      const data = await postCart('/cart/change.js', { id: lineKey, quantity, selling_plan: sellingPlanId || null });
      applyResponse(data);
      if (variantId) clearLineError(variantId);
    }, { variantId });
  }
  // MARK:- In-cart bow attach: one ordinary line carrying the visible `Part of` property; never selling_plan (one-time even under a subscription parent). Properties aren't expressible in Shopify.actions.updateCart, so this stays on the direct /cart/add.js pipeline. quantity mirrors the parent's current qty; ctx.variantId is the PARENT's variant so a 422 lands on its error slot.
  function addBowVariant(variantId, quantity, parentVariantId, partOf) {
    return enqueue(async () => {
      const data = await postCart('/cart/add.js', { items: [{ id: variantId, quantity, properties: { 'Part of': partOf } }] });
      applyResponse(data);
      if (parentVariantId) clearLineError(parentVariantId);
      refreshRecommendations(true);
    }, { variantId: parentVariantId });
  }

  function updateAttribute(key, value) {
    return routeCartUpdate({ attributes: { [key]: value } }, 'cart', { source: 'cart-extras' });
  }

  // MARK:- Cart note grammar shared with cart-drawer.liquid: `**gift**` first, `BOW:SKU,SKU` as the LAST line, joined by one newline; both optional. NetSuite/WMS read the note, not line properties.
  function parseCartNote(note) {
    const raw = typeof note === 'string' ? note : '';
    const lines = raw.split('\n');
    const last = lines[lines.length - 1] || '';
    if (last.startsWith('BOW:')) return { gift: lines.slice(0, -1).join('\n').trim(), bow: last.trim() };
    return { gift: raw.trim(), bow: '' };
  }
  function composeCartNote(gift, bow) {
    return [gift, bow].filter(Boolean).join('\n');
  }
  function currentCartNote() {
    const root = document.querySelector('#cart');
    if (!root) return '';
    // MARK:- Decode one Liquid HTML-escape layer before composing notes for the cart API.
    try {
      const note = JSON.parse(root.dataset.asCartNote || '""') || '';
      const entities = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
      return note.replace(/&(?:amp|lt|gt|quot|#39);/g, (entity) => entities[entity]);
    } catch (_) { return ''; }
  }

  // MARK:- Derive deduped bowed SKUs in cart order from the rendered grouping.
  function expectedBowNote() {
    const skus = new Set();
    document.querySelectorAll('#cart [data-as-cart-line][data-as-cart-sku]:not([data-as-cart-child-of])').forEach((line) => {
      const li = line.closest('li');
      const bowChild = li && li.querySelector(':scope > [data-as-cart-children] [data-as-cart-bow-child]');
      const sku = line.dataset.asCartSku;
      if (!bowChild || !sku) return;
      skus.add(sku);
    });
    if (!skus.size) return '';
    return `BOW:${Array.from(skus).join(',')}`;
  }

  // MARK:- Reconcile stale BOW notes through cart actions, preserving the gift segment.
  let bowNoteInFlight = false;
  function reconcileBowNote() {
    if (bowNoteInFlight) return;
    const root = document.querySelector('#cart');
    if (!root || root.dataset.asCartNote == null) return;
    const { gift, bow } = parseCartNote(currentCartNote());
    const expected = expectedBowNote();
    if (expected === bow) return;
    bowNoteInFlight = true;
    routeCartUpdate({ note: composeCartNote(gift, expected) }, 'cart', { source: 'as-cart-bow-note' })
      .finally(() => { bowNoteInFlight = false; });
  }

  // MARK:- Strip astral characters, BMP pictographs except © ® ™, and emoji sequence joiners.
  const EMOJI_RE = /[\u{10000}-\u{10FFFF}]|(?![\u{A9}\u{AE}\u{2122}])\p{Extended_Pictographic}|[\u{200D}\u{FE0F}\u{20E3}]/gu;
  function stripEmoji(value) {
    return typeof value === 'string' ? value.replace(EMOJI_RE, '') : '';
  }
  // MARK:- Toggle merchant emoji-removal copy; blank settings strip silently.
  function toggleEmojiNote(sub, show) {
    const note = sub && sub.querySelector('[data-as-cart-extra-emoji-note]');
    if (note) note.hidden = !show;
  }

  // MARK:- Gift note is stored in the standard cart note as **note** for the ERP; the wrapper is never shown or counted. Preserves BOW and clears the legacy attribute. Strips emoji as a safety net behind the input-time strip.
  function updateGiftNote(value) {
    const trimmed = stripEmoji(value).trim();
    const gift = trimmed ? `**${trimmed}**` : '';
    const { bow } = parseCartNote(currentCartNote());
    return routeCartUpdate({ note: composeCartNote(gift, bow), attributes: { 'Gift Note': '' } }, 'cart', { source: 'cart-extras' });
  }

  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-as-cart]')) return;

    const remove = e.target.closest('[data-as-cart-remove]');
    if (remove) {
      e.preventDefault();
      const li = remove.closest('[data-as-cart-line]');
      if (!li) return;
      // MARK:- Line mutations address /cart/change.js by line KEY (not 1-based index) — the key doubles as the Storefront Actions line id.
      changeLineWithChildren(li, 0, li.dataset.asCartVariantId);
      return;
    }

    const inc = e.target.closest('[data-as-cart-qty-increment]');
    const dec = e.target.closest('[data-as-cart-qty-decrement]');
    if (inc || dec) {
      e.preventDefault();
      const btn = inc || dec;
      if (btn.disabled) return;
      const li = btn.closest('[data-as-cart-line]');
      if (!li) return;
      const input = li.querySelector('[data-as-cart-qty-input]');
      if (!input) return;
      // MARK:- Per-line max comes from the input's max attr (set in cart-item.liquid from cart_line_limit). Falls back to the body-level theme cap when unset.
      const { max: bodyMax } = readQtyLimits();
      const inputMax = parseInt(input.getAttribute('max'), 10);
      const lineMax = Number.isFinite(inputMax) && inputMax > 0 ? inputMax : bodyMax;
      const current = parseInt(input.value, 10) || 0;
      // MARK:- Clamp to [0, lineMax]. - at qty 1 sends qty=0 → Shopify removes the line (existing behavior). + above max no-ops.
      const next = Math.min(lineMax, Math.max(0, current + (inc ? 1 : -1)));
      if (next === current) return;
      input.value = next;
      refreshLineQtyButtonState(li, next);
      // MARK:- Keep bow child quantities paired with the parent through the actions channel.
      changeLineWithChildren(li, next, li.dataset.asCartVariantId);
      return;
    }

    const quickAdd = e.target.closest('[data-as-cart-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      if (quickAdd.disabled) return;
      const variantId = quickAdd.dataset.asCartVariantId;
      if (!variantId) return;
      // MARK:- Bow quick-add mirrors its parent quantity; the parent supplies its variant for error routing; the button supplies the Part of title.
      const parentLineKey = quickAdd.dataset.asCartParentLineKey;
      if (parentLineKey) {
        const parentLine = quickAdd.closest('li')?.querySelector(':scope > [data-as-cart-line]');
        const partOf = quickAdd.dataset.asCartPartOf;
        if (!parentLine || !partOf) return;
        const parentQtyInput = parentLine.querySelector('[data-as-cart-qty-input]');
        const qty = parentQtyInput ? parseInt(parentQtyInput.value, 10) || 1 : 1;
        quickAdd.disabled = true;
        quickAdd.setAttribute('aria-busy', 'true');
        addBowVariant(variantId, qty, parentLine.dataset.asCartVariantId, partOf).finally(() => {
          quickAdd.disabled = false;
          quickAdd.removeAttribute('aria-busy');
        });
        return;
      }
      // MARK:- Busy state until the queued add settles — guards double-click duplicate adds. Re-enabling a node the recs refetch replaced is a harmless no-op.
      quickAdd.disabled = true;
      quickAdd.setAttribute('aria-busy', 'true');
      addVariant(variantId, 1).finally(() => {
        quickAdd.disabled = false;
        quickAdd.removeAttribute('aria-busy');
      });
      return;
    }

    // MARK:- Re-opening a cart-extra sub-drawer (Gift note + / Edit) resets its emoji note.
    const opener = e.target.closest('[data-as-drawer]');
    if (opener) toggleEmojiNote(document.getElementById(opener.dataset.asDrawer), false);

    const confirm = e.target.closest('[data-as-cart-extra-confirm]');
    if (confirm) {
      e.preventDefault();
      const sub = confirm.closest('.drawer__sub');
      if (!sub) return;
      const ta = sub.querySelector('[data-as-cart-extra-textarea]');
      if (!ta) return;
      const key = confirm.dataset.asCartExtraKey || ta.dataset.asCartExtraKey;
      const attributeName = attributeNameForKey(key);
      if (!attributeName) return;
      // MARK:- Strip emoji before saving either cart extra.
      const value = stripEmoji(ta.value);
      if (key === 'gift-note') updateGiftNote(value);
      else updateAttribute(attributeName, value);
      // MARK:- Close the sub-drawer immediately for snappy feedback; the cart
      // row's is-filled state updates when applyResponse → smartMerge runs.
      // MARK:- Hide the emoji note when the sub-drawer is done.
      toggleEmojiNote(sub, false);
      sub.classList.remove('is-open');
      return;
    }

    const removeAttr = e.target.closest('[data-as-cart-extra-remove]');
    if (removeAttr) {
      e.preventDefault();
      const key = removeAttr.dataset.asCartExtraRemove;
      const attributeName = attributeNameForKey(key);
      if (!attributeName) return;
      // MARK:- Remove clears the gift segment and legacy attribute while preserving bows.
      if (key === 'gift-note') updateGiftNote('');
      else updateAttribute(attributeName, '');
      // MARK:- If Remove was clicked from inside the sub-drawer, close it too;
      // Remove from the main cart row leaves the user in place.
      const subFromRemove = removeAttr.closest('.drawer__sub');
      // MARK:- Hide the emoji note when the sub-drawer is done.
      if (subFromRemove) {
        toggleEmojiNote(subFromRemove, false);
        subFromRemove.classList.remove('is-open');
      }
    }
  });

  document.addEventListener('change', (e) => {
    // MARK:- Purchase-type select — POST the chosen selling_plan (empty = one-time) plus the line's current quantity (required, else change.js resets it to 1) and refresh the drawer.
    const planSelect = e.target.closest('[data-as-cart-selling-plan-input]');
    if (planSelect) {
      const li = planSelect.closest('[data-as-cart-line]');
      if (!li || !li.closest('[data-as-cart]')) return;
      const qtyInput = li.querySelector('[data-as-cart-qty-input]');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      changeLineSellingPlan(li.dataset.asCartLineKey, qty, planSelect.value, li.dataset.asCartVariantId);
      return;
    }

    const input = e.target.closest('[data-as-cart-qty-input]');
    if (!input) return;
    const li = input.closest('[data-as-cart-line]');
    if (!li || !li.closest('[data-as-cart]')) return;
    const { max: bodyMax } = readQtyLimits();
    const inputMax = parseInt(input.getAttribute('max'), 10);
    const lineMax = Number.isFinite(inputMax) && inputMax > 0 ? inputMax : bodyMax;
    const qty = Math.min(lineMax, Math.max(0, parseInt(input.value, 10) || 0));
    if (qty !== parseInt(input.value, 10)) input.value = qty;
    refreshLineQtyButtonState(li, qty);
    // MARK:- Keep bow child quantities paired with the parent through the actions channel.
    changeLineWithChildren(li, qty, li.dataset.asCartVariantId);
  });

  // MARK:- Keyboard parity for the readonly cart-line qty input — ArrowUp / ArrowDown click the corresponding +/- button (which respects the disabled state).
  document.addEventListener('keydown', (e) => {
    const input = e.target.closest('[data-as-cart-qty-input]');
    if (!input || !input.closest('[data-as-cart]')) return;
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const li = input.closest('[data-as-cart-line]');
    if (!li) return;
    const btn = li.querySelector(e.key === 'ArrowUp' ? '[data-as-cart-qty-increment]' : '[data-as-cart-qty-decrement]');
    if (btn && !btn.disabled) btn.click();
  });

  // MARK:- Strip emoji live with caret preservation, skip IME composition, and enforce minimum length.
  document.addEventListener('input', (e) => {
    const ta = e.target.closest('[data-as-cart-extra-textarea]');
    if (!ta) return;
    const sub = ta.closest('.drawer__sub');
    if (!sub) return;
    if (!e.isComposing) {
      const cleaned = stripEmoji(ta.value);
      if (cleaned !== ta.value) {
        const caret = stripEmoji(ta.value.slice(0, ta.selectionStart)).length;
        ta.value = cleaned;
        ta.setSelectionRange(caret, caret);
        toggleEmojiNote(sub, true);
      }
    }
    const confirm = sub.querySelector('[data-as-cart-extra-confirm]');
    if (!confirm) return;
    const min = parseInt(ta.dataset.asCartExtraMin, 10) || 3;
    confirm.disabled = ta.value.trim().length < min;
  });

  document.addEventListener('cart:added', (e) => {
    // MARK:- If the dispatcher already POSTed with sections=cart-drawer, apply
    // the response synchronously (no second round trip, no open-stale-then-update flicker).
    const response = e.detail && e.detail.response;
    if (response && response.sections && response.sections[SECTION_CART]) {
      applyResponse(response);
    } else {
      refreshFromCart();
    }
  });

  // MARK:- PDP add-to-cart 422 path dispatches cart:line-error with { variantId, message } so the matching cart line's [data-as-cart-limit-text] swaps to the error string. eagerlyCorrectCartLine in component-product-form.js still handles the qty input + button state. Falls through to drawer-level error when no line matches.
  document.addEventListener('cart:line-error', (e) => {
    if (!e.detail) return;
    showLineError(e.detail.variantId, e.detail.message || '');
  });

  // MARK:- Cooperative cart-mutation hooks for third-party apps (GWP, subscription, etc.).
  // Any app that fires one of these events after mutating the cart triggers a smart-merge refresh.
  // Free integration surface — no fetch/XHR wrapping, no polling.
  ['cart:updated', 'cart:refresh', 'cart:changed'].forEach((name) => {
    document.addEventListener(name, () => refreshFromCart());
  });

  function attributeNameForKey(key) {
    if (key === 'gift-note') return 'Gift Note';
    if (key === 'gift-wrapping') return 'Gift Wrapping';
    return null;
  }

  // MARK:- Heal a stale BOW: note once on page load.
  function init() { refreshRecommendations(); reconcileBowNote(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
